/**
 * Feature flags used throughout the application
 *
 * @example
 * ```tsx
 * import { FEATURE_FLAGS } from '@/common/domain/constants/featureFlags';
 *
 * // Using in a component
 * const isFeatureEnabled = useFeatureFlag(FEATURE_FLAGS.JOBS_TIMELINE_TAB);
 *
 * // Using in redirect hook
 * useFeatureFlagRedirect({
 *   flagKey: FEATURE_FLAGS.JOBS_TIMELINE_TAB,
 *   paramKey: 'tab',
 *   paramValue: 'timeline',
 * });
 * ```
 */
export const FEATURE_FLAGS = {
  /* Jobs */

  /**
   * Controls the visibility of the Timeline tab in the Jobs section
   */
  JOBS_TIMELINE_TAB: 'jobs-timeline-tab',
  /**
   * Controls the visibility of the Month option in the Jobs Timeline tab
   */
  JOBS_TIMELINE_TAB_MONTH_OPTION: 'jobs.timelineTab.monthOption.enabled',
  /**
   * Controls the visibility of the Jobs Details page
   */
  JOBS_DETAILS_PAGE: 'jobs-details-page',
  /**
   * Controls the visibility of the ERP Job Details page (new card-based layout)
   */
  JOBS_ERP_DETAILS_PAGE_NEW_LOOK_AND_FEEL: 'jobs.erp.details.page.new.look.and.feel',
  /**
   * Controls the visibility of the Dispatch tab in the Jobs section
   */
  JOBS_DISPATCH_TAB: 'jobs-dispatch-tab',
  /**
   * Controls the visibility of the Map Overview tab in the Jobs section
   */
  JOBS_MAP_OVERVIEW_TAB: 'jobs-map-overview-tab',
  /**
   * Controls the visibility of the Calendar tab in the Jobs section
   */
  JOBS_CALENDAR_TAB: 'jobs-calendar-tab',

  /**
   * Controls the grouping functionality in the Jobs table
   */
  JOBS_TABLE_GROUPING: 'jobs-table-grouping',
  /**
   * Controls whether to stay on the current tab after resource assignment in Jobs
   */
  JOBS_RESOURCE_ASSIGNMENT_STAY_ON_TAB: 'jobs.resourceAssignment.stayOnTab',
  /**
   * Controls whether to show job details form after adding a new job
   */
  JOBS_ADD_NEW_SHOW_DETAILS: 'jobs.addNew.showDetails',
  /**
   * Controls whether to use RaptorJobCreateModal instead of JobDrawer for job creation/editing
   */
  JOBS_ERP_CREATE_MODAL_NEW_LOOK_AND_FEEL: 'jobs.erp.create.modal.new.look.and.feel',
  /**
   * Controls the visibility of employee filters in the Add Crew dialog for Jobs
   */
  JOBS_RESOURCE_GROUPS_EMPLOYEE_FILTERS: 'job.resourceGroups.employeeFilters',

  /* Job Sites */
  /**
   * Controls whether the Job Sites module appears in the Jobs sidebar and Jobs page tabs (TROC-317).
   * Off in ERP embed environments so Job Sites can be fully hidden from host chrome.
   */
  JOBSITES_MODULE_ENABLED: 'jobSites.module.enabled',
  /**
   * Controls whether to stay on the current tab after resource assignment in JobSites
   */
  JOBSITES_RESOURCE_ASSIGNMENT_STAY_ON_TAB: 'jobsites.assignment.stayOnTab',
  /**
   * Controls whether to show job site details form after adding a new job site
   */
  JOBSITES_ADD_NEW_SHOW_DETAILS: 'jobsites.addNew.showDetails',
  /**
   * Controls the visibility of the Overview tab in JobSite details
   */
  JOBSITES_OVERVIEW_TAB: 'jobsites.overview.tab.enabled',

  /**
   * Controls the visibility of the Permits tab and card in JobSite V2
   */
  JOBSITES_V2_PERMITS_ENABLED: 'jobsites.v2.permits.enabled',

  /* Raptor Jobs */
  /**
   * Controls the visibility of the Raptor Jobs tab
   */
  RAPTOR_JOBS_TAB: 'raptor-jobs-tab',

  /**
   * Controls whether the Create Raptor Job flow uses the mocked REST API
   * true -> POST to mock base at /mock/...; false -> POST to real /api/...
   */
  RAPTOR_JOBS_SINGLE_DAY_CREATE_MOCK_API_ENABLED: 'raptor-jobs-single-day-create-mock-api-enabled',
  /**
   * Shows certification compliance indicators (green/orange) on equipment and crew during New Job create flow
   */
  RAPTOR_JOBS_CREATE_RESOURCE_COMPLIANCE_ENABLED: 'raptorJobs.create.resourceCompliance.enabled',

  /* Raptor Jobs */
  /**
   * Controls the visibility of the Summary in raptor job details
   */
  RAPTOR_JOBS_DETAILS_SUMMARY: 'raptorJob.details.summary.enabled',
  /**
   * Controls the visibility of the Site Info in raptor job details
   */
  RAPTOR_JOBS_DETAILS_SITE_INFO: 'raptorJob.details.siteInfo.enabled',
  /**
   * Controls the visibility of the Tickets overview in raptor job details
   */
  RAPTOR_JOBS_DETAILS_TICKETS_OVER_VIEW: 'raptorJob.details.ticketsOverview.enabled',
  /**
   * Controls the visibility of the  Customer information in raptor job details
   */
  RAPTOR_JOBS_DETAILS_CUSTOMER_INFO: 'raptorJob.details.customerInfo.enabled',
  /**
   * Controls the visibility of the  Contacts in raptor job details
   */
  RAPTOR_JOBS_DETAILS_CONTACTS: 'raptorJob.details.contacts.enabled',

  /**
   * Controls whether RaptorJob creation uses mocked REST endpoint
   * When enabled, POSTs to /mock/api/v3/raptor-jobs/create-multi-day-job
   */
  RAPTOR_JOBS_CREATE_MULTI_DAY_JOB_MOCK_API_ENABLED: 'raptor-jobs-create-multi-day-job-mock-api-enabled',
  /**
   * Controls the visibility of the Manage Permits & Certifications button on Job Details
   */
  RAPTOR_JOBS_DETAILS_MANAGE_REQUIREMENTS: 'raptorJob.details.manageRequirements.enabled',

  RAPTOR_JOB_TICKET_OVERVIEW: 'raptorJob.ticket.overview.enabled',
  /**
   * Controls the visibility of the Ticket Group in raptor job ticket details
   */
  RAPTOR_JOB_TICKET_GROUP: 'raptorJob.ticket.group.enabled',
  /**
   * Controls the visibility of the Signatures section in raptor job ticket details
   */
  RAPTOR_JOB_TICKET_SIGNATURES: 'raptorJob.ticket.signatures.enabled',
  /**
   * Controls the visibility of the Comments section in raptor job ticket details
   */
  RAPTOR_JOB_TICKET_COMMENTS: 'raptorJob.ticket.comments.enabled',
  /**
   * Controls whether ticket editing functionality is enabled
   */
  RAPTOR_JOB_TICKET_EDIT_ENABLED: 'raptorJob.ticket.edit.enabled',
  /**
   * Controls the visibility of the Open In New icon button in the ticket header
   */
  JOB_TICKET_DETAILS_BUTTON: 'job.ticket.details.button',
  /**
   * Controls the visibility of the Resource Section card in raptor job ticket details
   */
  JOB_TICKET_RESOURCE_SECTION: 'job.ticket.resourceSection',
  /**
   * Controls whether ticket creation via API is enabled (mocked/new flow toggle)
   */
  /**
   * Controls the visibility of the Open In New icon button in the ticket header
   */
  RAPTOR_JOB_TICKETS_CREATE_MOCK_API_ENABLED: 'raptor-job-tickets-create-mock-api-enabled',
  /**
   * Controls the visibility of the Time Information card in raptor job ticket details
   */
  JOB_TICKET_TIME_INFORMATION: 'job.ticket.timeInformation.enabled',
  /**
   * Controls the visibility of the "All" tab in the Job Ticket Time Information card
   */
  JOB_TICKET_TIME_INFORMATION_ALL_TAB: 'job.ticket.timeInformation.allTab.enabled',
  /**
   * Controls the visibility of the Signatures card in raptor job ticket details
   */
  JOB_TICKET_SIGNATURES_CARD: 'job.ticket.signaturesCard.enabled',
  /* Employees */
  /**
   * Controls whether the Employees module appears in the sidebar (TROC-317).
   * Off for User-Management-only / embed orgs so the whole module can be hidden.
   */
  EMPLOYEES_MODULE_ENABLED: 'employees.module.enabled',
  /**
   * Controls the visibility of the Out of Office tab in the Employees section
   */
  EMPLOYEES_AVAILABILITY_TAB: 'employees-availability-tab',
  /**
   * Controls the visibility of the Restrictions tab in the Employees section
   */
  EMPLOYEES_RESTRICTIONS_TAB: 'employees-restrictions-tab',
  /**
   * Controls the visibility of the Employees Management page content
   */
  EMPLOYEES_MANAGEMENT_PAGE: 'employees-management-page',
  /**
   * Controls the use of the Out of Office Events Mock GraphQL API for employees
   */
  EMPLOYEES_OOO_EVENTS_MOCK_API: 'employees-ooo-events-mock-api',
  /**
   * Controls whether to redirect to employee details page after creating new employee
   */
  EMPLOYEES_ADD_NEW_SHOW_DETAILS: 'employees.addNew.showDetails',

  /**
   * Controls whether to stay on the current tab after editing employee assignments
   */
  EMPLOYEES_RESOURCE_ASSIGNMENT_STAY_ON_TAB: 'employees.assignment.stayOnTab',

  /**
   * Controls whether to display the v2 employee details page
   */
  EMPLOYEES_DETAILS_V2_ENABLED: 'employees.details.v2.enabled',

  /**
   * Controls the visibility of the Notes component in Employee V2 Overview tab
   */
  EMPLOYEES_V2_DETAILS_NOTES_ENABLED: 'employees.v2.details.notes.enabled',

  /**
   * Controls the visibility of the Job History tab in Employee V2
   */
  EMPLOYEES_V2_JOB_HISTORY_TAB_ENABLED: 'employees.v2.jobHistory.tab.enabled',

  /**
   * Controls the visibility of the Clocking tab in Employee V2
   */
  EMPLOYEES_V2_CLOCKING_TAB_ENABLED: 'employees.v2.clocking.tab.enabled',

  /**
   * Controls the visibility of the Out of Office tab in Employee V2
   */
  EMPLOYEES_V2_OUT_OF_OFFICE_TAB_ENABLED: 'employees.v2.outOfOffice.tab.enabled',

  /**
   * Controls the visibility of the Restrictions tab in Employee V2
   */
  EMPLOYEES_V2_RESTRICTIONS_TAB_ENABLED: 'employees.v2.restrictions.tab.enabled',

  /**
   * Controls the visibility of the Changes tab in Employee V2
   */
  EMPLOYEES_V2_CHANGES_TAB_ENABLED: 'employees.v2.changes.tab.enabled',

  /**
   * Controls the visibility of the Certifications tab and card in Employee V2
   */
  EMPLOYEES_V2_CERTIFICATIONS_ENABLED: 'employees.v2.certifications.enabled',

  /* Customers */
  /**
   * Controls whether the Customers module appears under Sales in the sidebar (TROC-317).
   * Off in ERP embed environments so Customers can be fully hidden from host chrome.
   */
  CUSTOMERS_MODULE_ENABLED: 'customers.module.enabled',
  /**
   * Controls whether to redirect to customer details page after creating new customer
   */
  CUSTOMERS_ADD_NEW_SHOW_DETAILS: 'customers.addNew.showDetails',

  /**
   * Controls whether to stay on the current tab after editing customer assignments
   */
  CUSTOMERS_RESOURCE_ASSIGNMENT_STAY_ON_TAB: 'customers.assignment.stayOnTab',

  /**
   * Controls the visibility of the new customer management interface with improved UX and bento layouts
   */
  CUSTOMERS_VIEW_ENABLED: 'customers.view.enabled',

  /**
   * Controls the visibility of the Notes component in Customers V2 Overview tab
   */
  CUSTOMERS_V2_NOTES_ENABLED: 'customers.v2.notes.enabled',

  /**
   * Controls the visibility of the Requirements tab in Customers V2
   */
  CUSTOMERS_V2_REQUIREMENTS_TAB_ENABLED: 'customers.v2.requirements.tab.enabled',

  /* Quotes */
  /**
   * Controls the visibility of the Quotes module in the Sales section
   */
  QUOTES_MODULE_ENABLED: 'quotes.module.enabled',

  /* Equipments */

  /**
   * Controls whether the Equipment module appears in the sidebar (TROC-317).
   * Off for User-Management-only / embed orgs so the whole module can be hidden.
   */
  EQUIPMENT_MODULE_ENABLED: 'equipment.module.enabled',
  /**
   * Controls the visibility of the Out of Service tab in the Equipments section
   */
  EQUIPMENT_OUT_OF_SERVICE_TAB: 'equipment-out-of-service-tab',
  /**
   * Controls the visibility of the Equipment Management page content
   */
  EQUIPMENT_MANAGEMENT_PAGE: 'equipment-management-page',
  /**
   * Controls the use of the Out of Service Events Mock GraphQL API for equipments
   */
  EQUIPMENTS_OOS_EVENTS_MOCK_API: 'equipments-oos-events-mock-api',
  /**
   * Controls whether to stay on the current tab after editing equipment assignments
   */
  EQUIPMENT_RESOURCE_ASSIGNMENT_STAY_ON_TAB: 'equipment.assignment.stayOnTab',

  /**
   * Controls whether to redirect to equipment details page after creating new equipment
   */
  EQUIPMENT_ADD_NEW_SHOW_DETAILS: 'equipments.addNew.showDetails',

  /**
   * Controls the visibility of the Overview tab in Equipment details
   */
  EQUIPMENT_OVERVIEW_TAB: 'equipments.overview.tab.enabled',

  /**
   * Controls the visibility of the Certifications tab and card in Equipment V2
   */
  EQUIPMENTS_V2_CERTIFICATIONS_ENABLED: 'equipments.v2.certifications.enabled',

  /**
   * Controls the visibility of the Job History tab in Equipment V2
   */
  EQUIPMENTS_V2_JOB_HISTORY_TAB_ENABLED: 'equipments.v2.jobHistory.tab.enabled',

  /**
   * Controls the visibility of the Job History tab in Equipment V2
   */
  EQUIPMENTS_V2_JOB_TICKETS_TAB_ENABLED: 'equipments.v2.jobTickets.tab.enabled',

  /**
   * Controls the visibility of the Maintenance tab in Equipment V2
   */
  EQUIPMENTS_V2_MAINTENANCE_TAB_ENABLED: 'equipments.v2.maintenance.tab.enabled',

  /**
   * Controls the visibility of the Out of Service tab in Equipment V2
   */
  EQUIPMENTS_V2_OUT_OF_SERVICE_TAB_ENABLED: 'equipments.v2.outOfService.tab.enabled',

  /**
   * Controls the visibility of the Changes tab in Equipment V2
   */
  EQUIPMENTS_V2_CHANGES_TAB_ENABLED: 'equipments.v2.changes.tab.enabled',

  /**
   * Controls the use of Orval-generated REST API implementation
   */
  ORVAL_ENABLED: 'orval.enabled',
  /**
   * Controls whether the Analytics module appears in the sidebar (TROC-317).
   * Off for User-Management-only / embed orgs so the whole module can be hidden.
   */
  ANALYTICS_MODULE_ENABLED: 'analytics.module.enabled',
  /**
   * Controls the visibility of the Analytics Dashboard tab
   */
  ANALYTICS_DASHBOARD_TAB: 'analytics-dashboard-tab',
  /**
   * Controls the visibility of the Analytics Reports tab
   */
  ANALYTICS_REPORTS_TAB: 'analytics-reports-tab',

  /**
   * Controls the Power BI integration visibility in Analytics Dashboard and Reports
   */
  POWERBI_ENABLED: 'powerbi.enabled',

  /**
   * Controls the use of the Attachments Mock API Server
   */
  ATTACHMENTS_MOCK_API_SERVER: 'attachments-mock-api-server',

  JOBS_NOTIFICATIONS: 'jobs-notification',

  /**
   * Controls the visibility and functionality of the Notify Crew button in Jobs
   */
  NOTIFY_CREW_BUTTON_ENABLED: 'notifyCrew.button.enabled',

  /**
   * Controls whether keyboard shortcuts/hotkeys are enabled across the application
   */
  ENABLE_HOTKEYS: 'enable-hotkeys',

  /**
   * Controls the visibility of the Dashboard tab
   */
  DASHBOARD_TAB: 'dashboard',
  /**
   * Controls the visibility of the floating action button (GlobalFAB) component
   */
  SHOW_FLOATING_ACTION_BUTTON: 'show-floating-action-button',

  /**
   * Controls the visibility of data table filters and custom styling
   */
  DISPLAY_DATA_TABLE_FILTER: 'display-data-table-filter',
  /**
   * Controls the visibility of the Job Communications menu
   */
  JOB_COMMUNICATIONS_MENU: 'job-communications-menu',
  /**
   * Controls the visibility of the Timesheets page
   */
  TIMESHEETS_PAGE: 'timesheets-page',

  /**
   * Controls the visibility of the Activities tab in the Timesheets page
   */
  TIMESHEETS_ACTIVITIES_TAB: 'timesheets-page-activities-tab',

  /**
   * Controls the visibility of the Pay Periods tab in the Timesheets page
   */
  TIMESHEETS_PAY_PERIODS_TAB: 'timesheets-page-pay-periods-tab',

  /**
   * Controls the visibility of the Overtime Rules tab in the Timesheets page
   */
  TIMESHEETS_OVERTIME_RULES_TAB: 'timesheets-page-overtime-rules-tab',

  CERTPERM_PERMITS: 'certperm.permits.enabled',

  /**
   * Controls the visibility of the Daily Time Card tab in the Timesheets section
   */
  TIMESHEETS_DAILY_TIME_CARD_TAB: 'timesheets-daily-time-card-tab',

  /**
   * Controls the use of the Daily Timecard Mock API Server
   */
  TIMESHEETS_DAILY_TIMECARD_MOCK_API: 'timesheets.dailyTimecard.mock.enabled',

  /**
   * Controls the navigation guard functionality with draft badge and undo button in JobForm
   */
  JOBS_NAVIGATION_GUARD_ENABLED: 'jobs.navigationGuard.enabled',

  /**
   * Controls the visibility of Special Flag 1, 2, 3 fields and Vendor section in job forms
   */
  JOBS_HIDE_SPECIAL_FLAGS_AND_VENDOR: 'JOBS_HIDE_SPECIAL_FLAGS_AND_VENDOR',

  /* Auth */
  /**
   * Controls whether Forgot/Reset Password entry points are visible in the auth flows
   */
  FORGOT_AND_RESET_PASSWORD: 'forgot-and-reset-password',
  /**
   * Controls the visibility of the "My account" menu item in the user navigation menu
   */
  AUTH_MY_ACCOUNT_ENABLED: 'auth.myAccount.enabled',

  /* User Management */
  /** Master kill switch — controls sidebar nav + all routes */
  USER_MANAGEMENT_PAGE: 'userManagement.enabled',
  /** Controls Roles tab visibility */
  USER_MANAGEMENT_ROLES: 'userManagement.roles',
  /** Controls Approvals tab visibility */
  USER_MANAGEMENT_APPROVALS: 'userManagement.approvals',
  /** Controls Audit Log tab visibility */
  USER_MANAGEMENT_AUDIT: 'userManagement.audit',
  /** Controls Snapshots tab visibility */
  USER_MANAGEMENT_SNAPSHOTS: 'userManagement.snapshots',
  /** Reserved for Quarterly Review feature */
  USER_MANAGEMENT_QUARTERLY_REVIEW: 'userManagement.quarterlyReview',
  /** Gates the Add User button + modal in the User Management Users tab. Off in prod until phase 2 (TROC-100). */
  USER_MANAGEMENT_USERS_ADD_NEW_ENABLED: 'userManagement.users.addNew.enabled',
  /**
   * Gates the "Databases" entry on the per-row actions menu (3-dot kebab) in the Users grid.
   * The action opens a drawer for changing a user's database membership — a feature that isn't
   * built end-to-end yet, so the menu item is hidden by default until it's ready. Flip to true
   * in LaunchDarkly for engineering / internal validation; the column itself is unaffected.
   */
  USER_MANAGEMENT_USERS_DATABASES_ACTION_ENABLED: 'userManagement.users.databasesAction.enabled',
  /** Gates delegation UI (header button, banners, admin row action) — TROC-178 */
  USER_MANAGEMENT_DELEGATION: 'userManagement.delegation',

  /* CRM Module (raptor-crm-fe) */

  /**
   * Master kill switch for the CRM micro-frontend.
   * Controls sidebar nav visibility and lazy-loading of the CRM remote application.
   * Enable per user / organisation in LaunchDarkly targeting rules.
   */
  CRM_MODULE_ENABLED: 'crm.module.enabled'
} as const;

/**
 * Defaults when a flag key is absent from the LD client (not yet created / not evaluated).
 *
 * Policy: modules that were historically always visible default **on** so production does
 * not blank nav until ops creates the LD flags (TROC-317). Modules that are opt-in
 * (e.g. `crm.module.enabled`, `quotes.module.enabled`) are deliberately omitted so they
 * remain hidden until enabled per-org in LaunchDarkly.
 */
export const FEATURE_FLAG_DEFAULTS: Partial<Record<string, boolean>> = {
  [FEATURE_FLAGS.CUSTOMERS_MODULE_ENABLED]: true,
  [FEATURE_FLAGS.JOBSITES_MODULE_ENABLED]: true,
  [FEATURE_FLAGS.EQUIPMENT_MODULE_ENABLED]: true,
  [FEATURE_FLAGS.EMPLOYEES_MODULE_ENABLED]: true,
  [FEATURE_FLAGS.ANALYTICS_MODULE_ENABLED]: true
};

/**
 * Type for feature flag keys
 */
export type FeatureFlagKey = keyof typeof FEATURE_FLAGS;

/**
 * Type for feature flag values
 */
export type FeatureFlagValue = (typeof FEATURE_FLAGS)[FeatureFlagKey];
